import { catchError, finalize } from 'rxjs';
import { Component, HostListener, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ID } from '@datorama/akita';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgForm } from '@angular/forms';

import { applyValues } from '@helpers/objects';
import { inputEmailPattern } from '@helpers/validation-patterns';
import {
  EntitiesService,
  EntityMemberCommunicationType,
  EntityMemberRoleType,
  entityMemberAccessLabel,
  entityMemberRoleLabels
} from '@state/entities';
import { FroalaHelper } from '@app/shared/services/vendor';
import { LaunchDarklyRepository } from '@state/launch-darkly';
import { MemberLike } from '@component/team';
import { MemberRoleType, MemberInviteParams } from '@state/memberships';
import { MessageBoxType } from '@component/message-box';
import { standardError$ } from '@helpers/error';

export interface MemberModalData {
  contactId?: ID;
  currentMember?: MemberLike;
  edit?: boolean;
  entityId: ID;
  enableCommunicationPreferences?: boolean;
  enableMemberSelectRoles: boolean;
  entityMemberRoles?: EntityMemberRoleType[];
  entityType: string;
  fundId?: ID;
  noTemplate: boolean;
  organizationId?: ID;
  primaryContact: boolean;
  providerId?: ID;
}

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-member-modal',
  styleUrls: ['member-modal.component.scss'],
  templateUrl: 'member-modal.component.html'
})
export class MemberModalComponent implements OnInit {
  enableQuestionnaireReview$ = this.launchDarklyRepository.enableQuestionnaireReview$;

  contactId: string;
  currentMember: MemberLike;
  edit = false;
  email: string;
  enableMemberSelectRoles: boolean;
  entityId: ID;
  entityMemberRoleType = EntityMemberRoleType;
  entityMemberRoleSelections = new Map<EntityMemberRoleType, boolean>();
  entityMemberTypeLabels = entityMemberRoleLabels;
  memberRoleTypeLabels = entityMemberAccessLabel;
  firstName: string;
  fundId: ID;
  inputEmailPattern = inputEmailPattern;
  lastName: string;
  member: MemberInviteParams = {
    communication_types: Object.values(EntityMemberCommunicationType),
    entity_member_roles: [],
    role: undefined
  };
  MemberRoleType = MemberRoleType;
  MessageBoxType = MessageBoxType;
  messages: string[];
  noTemplate: boolean;
  organizationId: ID;
  providerId: ID;
  saving: boolean;
  sendEmail = false;
  title: string;

  @HostListener('window:keyup.esc')
  close(): void {
    this.dialogRef.close();
  }

  constructor(
    private launchDarklyRepository: LaunchDarklyRepository,
    @Inject(MAT_DIALOG_DATA)
    public data: MemberModalData,
    public dialogRef: MatDialogRef<MemberModalComponent>,
    public entitiesService: EntitiesService,
    public froalaHelper: FroalaHelper,
    public snackBar: MatSnackBar
  ) {
    applyValues({
      data,
      keys: [
        'contactId',
        'currentMember',
        'edit',
        'fundId',
        'entityId',
        'enableMemberSelectRoles',
        'noTemplate',
        'organizationId',
        'providerId'
      ],
      obj: this
    });
  }

  ngOnInit() {
    let { currentMember } = this;
    if (currentMember) {
      this.email = currentMember.email;
      this.firstName = currentMember.first_name;
      this.lastName = currentMember.last_name;
      this.member.communication_types = currentMember.communication_types;
      this.member.role = currentMember.role;
      this.member.title = currentMember.title;
      this.title = currentMember.title;

      for (let role of currentMember.entity_member_roles) {
        this.entityMemberRoleSelections.set(role.role, true);
      }
    } else {
      this.entityMemberRoleSelections.set(EntityMemberRoleType.Administrator, true);
    }

    this.setEditMessages();
  }

  addMember(form: NgForm): void {
    if (form.form.invalid || this.saving) return;
    this.saving = true;
    let {
      contactId: contact_id,
      entityId,
      fundId: vehicle_id,
      member,
      organizationId: organization_id,
      providerId,
      sendEmail: send_email
    } = this;

    member.entity_member_roles = this.getTruthyKeysFromMap(this.entityMemberRoleSelections);

    // TODO: remove when member.role is deprecated
    member.role = this.getHighestMemberRoleType(member.entity_member_roles);

    this.entitiesService
      .addMember$({ contact_id, entityId, member, organization_id, providerId, send_email, vehicle_id })
      .pipe(
        catchError((error: HttpErrorResponse) => standardError$(error)),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Successfully added member.');
          this.dialogRef.close({ reload: true });
        }
      });
  }

  editMember(form: NgForm): void {
    if (form.form.invalid || this.saving) return;
    this.saving = true;

    let { contactId: contact_id, currentMember, entityId: entity_id, fundId: vehicle_id, member } = this;
    let entity_member_roles = this.getTruthyKeysFromMap(this.entityMemberRoleSelections);

    // TODO: remove when member.role is deprecated
    member.role = this.getHighestMemberRoleType(entity_member_roles);

    let memberId: ID = currentMember.id;

    this.entitiesService
      .updateTeamMember$({
        contact_id,
        entity_id,
        entity_member_roles,
        member,
        member_id: memberId,
        vehicle_id
      })
      .pipe(
        catchError((error: HttpErrorResponse) => standardError$(error)),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Successfully edited member.');
          this.dialogRef.close({ reload: true });
        }
      });
  }

  /**
   * Built as a work-around before we deprecate the `role` column on entity_members table
   * This returns the MemberRoleType from an array of entityMemberRoles
   * @param entityMemberRoles the entity member roles to select from
   */
  getHighestMemberRoleType(entityMemberRoles: EntityMemberRoleType[]): MemberRoleType {
    // keeping in mind that entity member roles are multi-select but member.role is just one
    // "access" type, for now we have a defined precedence order.
    let preferredOrder: MemberRoleType[] = [
      MemberRoleType.Owner,
      MemberRoleType.Administrator,
      MemberRoleType.Limited,
      MemberRoleType.Viewer
    ];

    let selectedRoles = new Set(entityMemberRoles);

    for (let memberRole of preferredOrder) {
      // type casting needed to bypass strict type checking
      // because EntityMemberRoleType contains more than MemberRoleType.
      if (selectedRoles.has(memberRole as unknown as EntityMemberRoleType)) {
        return memberRole;
      }
    }

    return MemberRoleType.Other; // Fallback if no match is found
  }

  getTruthyKeysFromMap<T>(map: Map<T, boolean>): T[] {
    return [...map].filter(([, value]) => value).map(([key]) => key);
  }

  setEditMessages(): void {
    let { entityMemberRoles, entityType, primaryContact } = this.data;

    let messages = [`To add new Owners to this ${entityType} please contact support@flowinc.com`];

    if (primaryContact && entityMemberRoles.includes(EntityMemberRoleType.Viewer)) {
      messages.push(
        `The selected user is the primary contact for this ${entityType} and cannot be set to "View Only"`
      );
    }

    if (this.currentMember?.role !== MemberRoleType.Viewer && this.member.role === MemberRoleType.Viewer) {
      messages.push(
        'If this user has any tasks currently assigned, they will be assigned to the primary contact'
      );
    }

    this.messages = messages;
  }
}
